import { useState, type FormEvent } from "react";
import { useNotify } from "ra-core";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { createCompany } from "@/modules/platform/platformApi";

/** Creates a company and emails its first admin a set-password invite. */
export const NewCompanyDialog = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const notify = useNotify();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [companyName, setCompanyName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    try {
      const { org_id } = await createCompany({
        company_name: companyName.trim(),
        admin_email: email.trim(),
        admin_first_name: firstName.trim(),
        admin_last_name: lastName.trim(),
      });
      await queryClient.invalidateQueries({ queryKey: ["platform"] });
      notify(`Company created. Invite sent to ${email.trim()}`, {
        type: "success",
      });
      onOpenChange(false);
      navigate(`/platform/companies/${org_id}`);
    } catch (error) {
      notify(
        error instanceof Error ? error.message : "Could not create company",
        {
          type: "error",
        },
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>New company</DialogTitle>
            <DialogDescription>
              Creates an empty workspace and emails its admin a link to set a
              password.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="platform-company-name">Company name</Label>
            <Input
              id="platform-company-name"
              value={companyName}
              onChange={(event) => setCompanyName(event.target.value)}
              required
              autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="platform-admin-first">Admin first name</Label>
              <Input
                id="platform-admin-first"
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="platform-admin-last">Admin last name</Label>
              <Input
                id="platform-admin-last"
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="platform-admin-email">Admin email</Label>
            <Input
              id="platform-admin-email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? <Loader2 className="size-4 animate-spin" /> : null}
              Create and invite
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
